import React from "react";
import { useController, useFormContext } from "react-hook-form";
import { ArrowRightAltIcon, ClearBadge, Color } from "@bonliva-crm/ui";
import {
  Box,
  Chip,
  IconButton,
  makeStyles,
  ThemeProvider,
  Typography
} from "@material-ui/core";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { NonCollapsibleFavFilter } from "./_base/non-collapsible-fav-filter";
import { useLocale } from "@hooks";
import { bonlivaTheme } from "@theme";
import { bonlivaPipelineTheme } from "@bonliva-crm/ui";
import ConsultantSelector from "@containers/VirtualSelector/ConsultantSelector";
import { ConsultantCardFragment } from "@bonliva-crm/graphql";

export type ConsultantNameFilterProps = FilterWithFavouriteProps;

export const ConsultantNameFilter = (props: ConsultantNameFilterProps) => {
  const { locale } = useLocale();
  const classes = useStyles();
  const title = props.label ?? locale.Consultants;

  const { resetField, setValue: setFormValue } = useFormContext();
  const {
    field,
    fieldState: { isDirty }
  } = useController({ name: props.name });

  const removeItem = (id: string) => {
    setFormValue(
      props.name,
      field.value.filter((v: ConsultantCardFragment) => v._id !== id),
      { shouldDirty: true }
    );
  };

  if (props.setFavorite) {
    return (
      // TODO: upd selector with new theme
      <ThemeProvider theme={bonlivaTheme}>
        <ConsultantSelector
          fullWidth
          slim
          multi
          onChange={(v: ConsultantCardFragment[]) => {
            setFormValue(props.name, v, { shouldDirty: true });
          }}
          value={field?.value ?? []}
          control={
            <ThemeProvider theme={bonlivaPipelineTheme}>
              <NonCollapsibleFavFilter
                favorite={props.favorite}
                onSetFavorite={props.setFavorite}
                title={title}
                isDirty={isDirty}
                isClearable={isDirty}
                onClear={(e) => {
                  resetField(props.name);
                  e.stopPropagation();
                  e.preventDefault();
                }}
                action={
                  <IconButton>
                    <ArrowRightAltIcon />
                  </IconButton>
                }
              >
                {field.value?.length > 0 && (
                  <Box display="flex" flexWrap="wrap" flex="1" gridGap="8px">
                    {field.value.map((v: ConsultantCardFragment) => (
                      <Chip
                        size="small"
                        key={v._id}
                        label={v.Name}
                        onDelete={(e) => {
                          e.stopPropagation();
                          removeItem(v._id);
                        }}
                      />
                    ))}
                  </Box>
                )}
              </NonCollapsibleFavFilter>
            </ThemeProvider>
          }
        />
      </ThemeProvider>
    );
  }

  return (
    // TODO: upd selector with new theme
    <ThemeProvider theme={bonlivaTheme}>
      <ConsultantSelector
        fullWidth
        slim
        multi
        onChange={(v: ConsultantCardFragment[]) =>
          setFormValue(props.name, v, { shouldDirty: true })
        }
        value={field?.value ?? []}
        control={
          <ClearBadge
            invisible={!field?.value?.length}
            onClear={(e) => {
              e.stopPropagation();
              resetField(props.name);
            }}
          >
            <div className={classes.favouriteButton}>
              <Typography variant="body1" style={{ color: Color.DetailsInput }}>
                {title}
              </Typography>

              <ArrowRightAltIcon fontSize="small" style={{ marginLeft: 8 }} />

              {field?.value?.length > 0 && (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  flex="1"
                  gridGap="8px"
                  ml={1}
                  alignItems={"center"}
                >
                  {field?.value?.map((el: ConsultantCardFragment, i: number) => {
                    if (i === 4) {
                      return "...";
                    }
                    if (i > 4) {
                      return null;
                    }
                    return (
                      <Chip
                        key={el._id}
                        size="small"
                        label={el.Name}
                        onDelete={(e) => {
                          e.stopPropagation();
                          const newValue = [...(field?.value ? field.value : [])];
                          newValue.splice(i, 1);
                          setFormValue(props.name, newValue, { shouldDirty: true });
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </div>
          </ClearBadge>
        }
      />
    </ThemeProvider>
  );
};

const useStyles = makeStyles({
  favouriteButton: {
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "4px",
    padding: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: Color.LightGray
    }
  }
});
